import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useState } from 'react';

import { FlexContainer } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { Input } from '@/ui/input/input';
import { BorderedTextArea } from '@/ui/textarea/bordered-text-area';
import { Button } from '@/ui/button/button';
import { askToJoinValidationSchema } from './ask-to-join-validation-schema';
import { useAskToJoin } from './use-ask-to-join.mutation';

type AskToJoinFormData = {
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  note?: string;
};

interface AskToJoinFormProps {
  linkId?: string;
  organizationName?: string;
  spotlightLinkId?: string;
}

export const AskToJoinForm = ({
  linkId,
  organizationName,
  spotlightLinkId,
}: AskToJoinFormProps) => {
  const { t } = useTranslation('ask-to-join');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { submitAskToJoin, isLoading, isSuccess } = useAskToJoin();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
  } = useForm<AskToJoinFormData>({
    resolver: zodResolver(askToJoinValidationSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: AskToJoinFormData) => {
    // Always use linkId since backend DTO only accepts linkId
    if (!linkId) {
      console.error('Link ID is required');
      return;
    }

    try {
      await submitAskToJoin({
        ...data,
        linkId,
        ...(spotlightLinkId ? { spotlightLinkId } : {}),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting ask to join request:', error);
    }
  };

  // Show success message if form was submitted successfully
  if (isSubmitted && isSuccess) {
    return (
      <FormContainer>
        <FormContent>
          <SuccessHeader>
            <SuccessIcon data-testid="ask-to-join-success-icon">✓</SuccessIcon>
            <SuccessTitle data-testid="ask-to-join-success-title">{t`success.title`}</SuccessTitle>
            <SuccessDescription data-testid="ask-to-join-success-description">
              {t('success.description', { organizationName })}
            </SuccessDescription>
          </SuccessHeader>
        </FormContent>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <FormContent>
        <Header>
          <Title>{t`title`}</Title>
          <Description>{t`description`}</Description>
        </Header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FieldsContainer>
            <NameFieldsContainer>
              <Input
                data-testid="ask-to-join-first-name"
                placeholder={t`form.firstName.placeholder`}
                {...register('firstName')}
                error={errors?.firstName?.message}
                disabled={isLoading}
              />

              <Input
                data-testid="ask-to-join-last-name"
                placeholder={t`form.lastName.placeholder`}
                {...register('lastName')}
                error={errors?.lastName?.message}
                disabled={isLoading}
              />
            </NameFieldsContainer>

            <Input
              data-testid="ask-to-join-email"
              type="email"
              placeholder={t`form.email.placeholder`}
              {...register('email')}
              error={errors?.email?.message}
              disabled={isLoading}
            />
            <Input
              data-testid="ask-to-join-position"
              placeholder={t`form.position.placeholder`}
              {...register('position')}
              error={errors?.position?.message}
              disabled={isLoading}
            />

            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <BorderedTextArea
                  id="ask-to-join-note"
                  placeholder={t`form.note.placeholder`}
                  value={field.value || null}
                  onChange={field.onChange}
                  height={80}
                  resize="vertical"
                />
              )}
            />
          </FieldsContainer>

          <ButtonContainer>
            <Button
              data-testid="ask-to-join-submit"
              variant="primary"
              isLoading={isLoading}
              disabled={!isDirty || !isValid || isLoading}
              fullWidth
              height={36}
              textCenter
            >
              {t`actions.submit`}
            </Button>
          </ButtonContainer>
        </Form>
      </FormContent>
    </FormContainer>
  );
};

const FormContainer = styled(FlexContainer)`
  padding: 12px 12px 40px 12px;
  max-width: 500px;
  width: 100%;
`;

const FormContent = styled(Stack)`
  width: 100%;
  gap: 32px;
`;

const Header = styled('div')`
  text-align: center;
`;

const Title = styled('h1')`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const Description = styled('p')`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.gray.c12};
  margin: 0;
  line-height: 1.5;
`;

const Form = styled('form')`
  width: 100%;
`;

const FieldsContainer = styled(Stack)`
  gap: 20px;
  margin-bottom: 32px;
`;

const NameFieldsContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ButtonContainer = styled('div')`
  width: 100%;
`;

const SuccessHeader = styled('div')`
  text-align: center;
  padding: 20px 0;
`;

const SuccessIcon = styled('div')`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.blue.c6};
  margin-bottom: 16px;
  font-weight: bold;
`;

const SuccessTitle = styled('h2')`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.basics.black};
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const SuccessDescription = styled('p')`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.gray.c12};
  margin: 0;
  line-height: 1.5;
`;
